import React, { useState, useEffect, useRef } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useSelector } from 'react-redux';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import FormControl from 'react-bootstrap/lib/FormControl';
import Checkbox from 'react-bootstrap/lib/Checkbox';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import { defaultMessages } from '../../../libs/i18n/default';
import FormDate from '../../../shared/components/FormDate';
import { CustomFormGroup, Form } from '../../../shared/components';
import { generateProfileElementObject } from '../../../shared/profileElementHelpers';
import { changeState, getAge, yesNoOptions } from '../../../shared/utilities';
import { pageValid, fieldsToValidate } from '../../../shared/validator';
import { guestProfileSubmit, updateVaccinationInfo } from '../actions';
import { ameOrUsa } from '../helpers'
import P from '../pages';
import CertificateUpload from './CertificateUpload';
import CheckboxOrRadioGroup from './CheckboxOrRadioGroup';
import withDefaults from './withDefaults'
import FormHeader from './FormHeader';
import Alert from './Alert';

const Medical = ({ content, dispatch, user, territory, page, environment, intl: { formatMessage } }) => {
  const thisPage = P.MEDICAL_PAGE;
  const vaccinationOptions = useSelector(state => state.vaccinationOptions)
  const vaccinationInfo = useSelector(state => state.vaccinationInfo)
  const vaccinationMandatory = useSelector(state => state.vaccinationMandatory)
  const showCovidVaccinationFields = territory.covid_vaccination_fields === true && !territory.disable_covid_questions
  const {
    state: vaccination_state,
    dose_one_on,
    dose_two_on,
    certificate_name,
    uploaded_certificate
  } = useSelector(state => state.vaccinationInfo)
  const [submitted, setSubmitted] = useState(false)
  const [fields, setFields] = useState({
    smoker: generateProfileElementObject('', 'preferences', 'smoker'),
    liveWithSmokers: generateProfileElementObject('', 'preferences', 'live_with_smokers'),
    alcohol: {
      ...generateProfileElementObject('', 'preferences', 'alcohol'),
      profileElement: getAge(user.dob) >= 21 && ameOrUsa(territory) && 'alcohol'
    },
    medical: generateProfileElementObject('', 'preferences', 'medical'),
    vaccinationState: vaccination_state || '',
    doseOneOn: dose_one_on || '',
    doseTwoOn: dose_two_on || '',
    certificate: '',
    certificateTermsAccepted: false
  })
  const certificateName = fields.vaccinationState === 'vaccinated' ? 'vaccination' : 'exemption'
  const certificateRequired = fields.vaccinationState === 'vaccinated' || fields.vaccinationState === 'exempt'
  const fieldsRef = useRef(fields)
  fieldsRef.current = fields

  useEffect(() => {
    fieldsToValidate(fields).forEach(f => {
      handleChange(f, fields[f].val, false)
    })
  }, [])

  useEffect(() => {
    if (!fields.vaccinationState) return
    if (fields.vaccinationState === 'vaccinated') return

    setFields({
      ...fieldsRef.current,
      doseOneOn: '',
      doseTwoOn: ''
    })

  }, [fields.vaccinationState])

  const handleChange = (key, val, resetDependentObject = true, useChangeState = true) => {
    if (!useChangeState) {
      setFields({...fieldsRef.current, [key]: val})
      return
    }

    changeState(
      fieldsRef.current,
      key,
      val,
      (newObj) => {
        setFields({...fieldsRef.current, ...newObj})
      },
      resetDependentObject
    )
  }

  const submit = async e => {
    e.preventDefault()
    setSubmitted(true)

    if (showCovidVaccinationFields === true) {
      await dispatch(updateVaccinationInfo({
        guest: {
          state: fields.vaccinationState,
          doseOneOn: fields.doseOneOn,
          doseTwoOn: fields.doseTwoOn
        },
        ...(fields.certificate && {
          certificate: {
            guest_id: user.guest.id,
            attachment: {
              name: `Covid ${_.capitalize(certificateName)} Certificate - ${user.name}`,
              kind: `covid_${certificateName}_certificate`,
              file: fields.certificate
            }
          }
        })
      }))
    }

    await dispatch(guestProfileSubmit({
      preferences: {
        smoker: fields.smoker.val,
        live_with_smokers: fields.liveWithSmokers.val,
        medical: fields.medical.val,
        alcohol: fields.alcohol.val
      }
    }, thisPage))
  }

  return (
    <Form page={page} thisPage={thisPage} back={true} skip={environment != 'production'} submitFunc={submit} disableSubmit={!pageValid(fields) || submitted || (certificateRequired && !fields.certificateTermsAccepted)} >
      <FormHeader title={content.pages.medical.title} />

      <CustomFormGroup field={fields.smoker}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesMedicalLabelsSmoke)}</ControlLabel>
        <FormControl id="smoker"
          type="select"
          componentClass="select"
          defaultValue={fields.smoker.val}
          onChange={e => handleChange(e.target.id, e.target.value)}>
          {yesNoOptions(formatMessage)}
        </FormControl>
      </CustomFormGroup>

      <CustomFormGroup field={fields.liveWithSmokers}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesMedicalLabelsLiveWithSmokers)}</ControlLabel>
        <FormControl id="liveWithSmokers"
          type="select"
          componentClass="select"
          defaultValue={fields.liveWithSmokers.val}
          onChange={e => handleChange(e.target.id, e.target.value)}>
          {yesNoOptions(formatMessage)}
        </FormControl>
      </CustomFormGroup>

      <Alert alert={content.pages.medical.smoking_info} />

      <CustomFormGroup field={fields.alcohol}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesMedicalLabelsAlcohol)}</ControlLabel>
        <FormControl id="alcohol"
          type="select"
          componentClass="select"
          defaultValue={fields.alcohol.val}
          onChange={e => handleChange(e.target.id, e.target.value)}>
          {yesNoOptions(formatMessage)}
        </FormControl>
      </CustomFormGroup>

      <CustomFormGroup field={fields.medical}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesMedicalLabelsMedicalConditions)}</ControlLabel>
        <FormControl id="medical"
          type="text"
          componentClass="textarea"
          defaultValue={fields.medical.val}
          onChange={e => handleChange(e.target.id, e.target.value)} />
      </CustomFormGroup>

      {showCovidVaccinationFields && (
        <div style={{ marginBottom: '1.2em', marginTop: '1.2em' }}>
          <hr />
          <CheckboxOrRadioGroup
            title={formatMessage(defaultMessages.clientHostPagesResidentsLabelsCovidVaccinationStatus)}
            setName={'vaccinationState'}
            type={'radio'}
            checkBoxStyle={'radio'}
            controlFunc={e => handleChange('vaccinationState', e.target.value, true, false)}
            options={vaccinationOptions}
            selectedOptions={[fields.vaccinationState]}
          />
          {fields.vaccinationState === 'vaccinated' && vaccinationMandatory && territory.code != 'NZ' && (
            <div className="form-group">
              <div className='row'>
                <div className='col-sm-6'>
                  <ControlLabel>{formatMessage(defaultMessages.clientHostPagesResidentsLabelsCovidVaccinationDose1)}</ControlLabel>
                  <FormDate
                    formDateId="doseOneOn"
                    setDate={date => handleChange('doseOneOn', date, true, false)}
                    date={fields.doseOneOn}
                  />
                </div>
                <div className='col-sm-6'>
                  <ControlLabel>{formatMessage(defaultMessages.clientHostPagesResidentsLabelsCovidVaccinationDose2)}</ControlLabel>
                  <FormDate
                    formDateId="doseTwoOn"
                    setDate={date => handleChange('doseTwoOn', date, true, false)}
                    date={fields.doseTwoOn}
                  />
                </div>
              </div>
            </div>
          )}
          {fields.vaccinationState && certificateRequired && (
            <>
              <CertificateUpload
                uploadedCertificate={uploaded_certificate}
                exitedCertificate={certificate_name}
                newCertificate={certificateName}
                vaccinationState={fields.vaccinationState}
                onChange={handleChange}
                dispatch={dispatch}
              />

              <Checkbox
                id="certificateTermsAccepted"
                checked={fields.certificateTermsAccepted}
                onChange={() => handleChange('certificateTermsAccepted', !fields.certificateTermsAccepted, true, false)}
                required
              >
                {ReactHtmlParser(formatMessage(
                  defaultMessages.clientHostPagesResidentsLabelsCovidTermsAccepted,
                  {name: territory.short_name}
                ))}
              </Checkbox>
            </>
          )}
          {vaccinationMandatory && content.pages.medical.info && <Alert alert={content.pages.medical.info} />}
        </div>
      )}
    </Form>
  )
}

export default withDefaults(Medical);
